/**
 * GA4 Events - Central dataLayer push and event config.
 * All event name, location, detail, and type are defined here and can be overridden or extended.
 */
(function () {
  'use strict';

  if (typeof window === 'undefined') return;

  var GA4_DEFERRED_KEY = 'ga4_deferred_event';

  /**
   * Push a GA4 event to dataLayer (for GTM) and to gtag if available (for direct GA4).
   * If clickTarget is provided and is a redirecting link, the payload is stored in sessionStorage
   * and will be pushed on the next page load (so the event is attributed to the destination page).
   * GTM: create a Custom Event trigger for our event names and a GA4 Event tag – see docs/GA4-GTM-SETUP.md.
   * @param {Object} payload - { event: string, location: string, detail: string, type: string }
   * @param {Element} [clickTarget] - Optional element that was clicked (e.g. e.target or e.target.closest('a')).
   *        If this is a redirecting link, payload is deferred to sessionStorage and flushed after redirect.
   */
  function pushGA4(payload, clickTarget) {
    if (!payload || typeof payload.event !== 'string') return;
    if (clickTarget && isRedirectingLink(clickTarget)) {
      pushGA4Deferred(payload);
      return;
    }
    doPushGA4(payload);
  }

  /**
   * Internal: perform the actual dataLayer/gtag push.
   */
  function doPushGA4(payload) {
    window.dataLayer = window.dataLayer || [];
    var eventParams = {
      event: payload.event,
      location: payload.location || '',
      detail: payload.detail || '',
      type: payload.type || ''
    };
    window.dataLayer.push(eventParams);
    // if (typeof window.gtag === 'function') {
    //   window.gtag('event', payload.event, {
    //     location: payload.location || '',
    //     detail: payload.detail || '',
    //     type: payload.type || ''
    //   });
    // }
    if (typeof console !== 'undefined' && console.log) {
      console.log('[GA4]', payload.event, payload);
    }
  }

  /**
   * Store payload in sessionStorage so it is pushed on the next page load (after redirect).
   * Used for clicks on links that navigate away.
   */
  function pushGA4Deferred(payload) {
    try {
      window.sessionStorage.setItem(GA4_DEFERRED_KEY, JSON.stringify(payload));
      if (typeof console !== 'undefined' && console.log) {
        console.log('[GA4] deferred (redirect)', payload.event, payload);
      }
    } catch (err) {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[GA4] deferred store failed', err);
      }
    }
  }

  /**
   * On page load: if a deferred event was stored before a redirect, push it to dataLayer and clear sessionStorage.
   */
  function flushDeferredGA4() {
    try {
      var raw = window.sessionStorage && window.sessionStorage.getItem(GA4_DEFERRED_KEY);
      if (!raw) return;
      var payload = JSON.parse(raw);
      if (payload && typeof payload.event === 'string') {
        doPushGA4(payload);
      }
      window.sessionStorage.removeItem(GA4_DEFERRED_KEY);
    } catch (err) {
      if (window.sessionStorage) window.sessionStorage.removeItem(GA4_DEFERRED_KEY);
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[GA4] flush deferred failed', err);
      }
    }
  }

  /**
   * True if el is (or is inside) an anchor that navigates to another page.
   * Links with href="#", "javascript:...", or empty are not considered redirecting.
   */
  function isRedirectingLink(el) {
    if (!el || !el.nodeName) return false;
    var a = el.nodeName.toLowerCase() === 'a' ? el : (el.closest && el.closest('a'));
    if (!a || !a.href) return false;
    var h = (a.getAttribute && a.getAttribute('href')) || '';
    if (!h || h === '#' || h.slice(0, 11).toLowerCase() === 'javascript:') return false;
    return true;
  }

  /**
   * Mark links that redirect with data-ga4-redirect so they can be identified in the DOM.
   * Call on DOMContentLoaded if you want the attribute set for all such links.
   */
  function markRedirectingLinks() {
    if (typeof document === 'undefined' || !document.querySelectorAll) return;
    var links = document.querySelectorAll('a[href]');
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      if (isRedirectingLink(a)) a.setAttribute('data-ga4-redirect', 'true');
    }
  }

  flushDeferredGA4();
  if (typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('DOMContentLoaded', markRedirectingLinks);
  }

  /**
   * Build a payload from a config key and optional dynamic detail/overrides.
   * @param {Object} base - Base config { event, location, detail, type }
   * @param {Object} [overrides] - Optional overrides (e.g. { detail: 'L1: ' + name })
   * @returns {Object} Payload for pushGA4
   */
  function buildPayload(base, overrides) {
    if (!base) return null;
    var p = {
      event: base.event,
      location: base.location,
      detail: base.detail,
      type: base.type
    };
    if (overrides && typeof overrides === 'object') {
      if (overrides.event != null) p.event = overrides.event;
      if (overrides.location != null) p.location = overrides.location;
      if (overrides.detail != null) p.detail = overrides.detail;
      if (overrides.type != null) p.type = overrides.type;
    }
    return p;
  }

  /**
   * Event config: event name, location, detail, type (all dynamic / single source of truth).
   * Change values here to update tracking across the site.
   */
  var GA4_EVENTS = {
    header: {
      hamburger: {
        event: 'nav_click',
        location: 'header_nav',
        detail: 'Open Menu',
        type: 'icon'
      },
      navLinkL1: {
        event: 'nav_click',
        location: 'header_nav',
        detail: 'L1: ',
        type: 'link'
      },
      navLinkL2: {
        event: 'nav_click',
        location: 'header_nav',
        detail: 'L2: ',
        type: 'link'
      },
      navLinkL3: {
        event: 'nav_click',
        location: 'header_nav',
        detail: 'L3: ',
        type: 'link'
      },
      logo: {
        event: 'nav_click',
        location: 'header_nav',
        detail: 'Home',
        type: 'logo'
      },
      cart: {
        event: 'nav_click',
        location: 'header_nav',
        detail: 'Cart',
        type: 'icon'
      },
      cartOpen: {
        event: 'ui_interaction',
        location: 'cart_drawer',
        detail: 'Open Cart',
        type: 'icon'
      },
      padFinder: {
        event: 'ui_interaction',
        location: 'header_nav',
        detail: 'Open Pad Finder',
        type: 'button'
      },
      promoCard: {
        event: 'promotion_click',
        location: 'header_nav',
        detail: 'Promo: ',
        type: 'promo_card'
      },
      mobileMenu: {
        expandCategory: {
          event: 'nav_click',
          location: 'header_nav',
          detail: 'Expand: ',
          type: 'button'
        },
        collapseCategory: {
          event: 'nav_click',
          location: 'header_nav',
          detail: 'Collapse: ',
          type: 'button'
        },
        submenuLink: {
          event: 'nav_click',
          location: 'header_nav',
          detail: 'Link: ',
          type: 'link'
        },
        changeCurrency: {
          event: 'ui_interaction',
          location: 'header_nav',
          detail: 'Change Currency',
          type: 'dropdown'
        },
        utilityButton: {
          event: 'ui_interaction',
          location: 'header_nav',
          detail: 'Click: ',
          type: 'button'
        }
      }
    },
    collection: {
      sortOpen: {
        event: 'ui_interaction',
        location: 'collection_controls',
        detail: 'Open Sort Menu',
        type: 'button'
      },
      sortClose: {
        event: 'ui_interaction',
        location: 'sort_panel',
        detail: 'Close Sort Menu',
        type: 'icon'
      },
      sortOption: {
        event: 'sort_click',
        location: 'sort_panel',
        detail: 'Sort: ',
        type: 'list_item'
      }
    },
    collectionFilter: {
      open: {
        event: 'ui_interaction',
        location: 'collection_controls',
        detail: 'Open Filter Menu',
        type: 'button'
      },
      close: {
        event: 'ui_interaction',
        location: 'filter_panel',
        detail: 'Close Filter Menu',
        type: 'icon'
      },
      inStockOnly: {
        event: 'filter_click',
        location: 'filter_panel',
        detail: 'Availability: In Stock Only',
        type: 'toggle'
      },
      priceRange: {
        event: 'filter_click',
        location: 'filter_panel',
        detail: 'Price Range: ',
        type: 'slider'
      },
      accordionExpand: {
        event: 'ui_interaction',
        location: 'filter_panel',
        detail: 'Expand: ',
        type: 'accordion'
      },
      checkboxOption: {
        event: 'filter_click',
        location: 'filter_panel',
        detail: '',
        type: 'checkbox'
      },
      applyFilters: {
        event: 'ui_interaction',
        location: 'filter_panel',
        detail: 'Apply Selected Filters',
        type: 'button'
      },
      sortOpen: {
        event: 'ui_interaction',
        location: 'filter_panel',
        detail: 'Open Sort Menu',
        type: 'dropdown'
      },
      sortOption: {
        event: 'sort_click',
        location: 'filter_panel',
        detail: 'Sort: ',
        type: 'list_item'
      }
    },
    slideshow: {
      heroBanner: {
        event: 'promotion_click',
        location: 'hero_banner',
        detail: '',
        type: 'promo_banner'
      }
    },
    collectionBanner: {
      heroImage: {
        event: 'promotion_click',
        location: 'collection_banner',
        detail: 'Banner: ',
        type: 'promo_banner'
      }
    },
    collectionCategories: {
      modelThumbnail: {
        event: 'promotion_click',
        location: 'collection_categories',
        detail: 'Model: ',
        type: 'promo_card'
      }
    },
    pdpGallery: {
      thumbnailSelect: {
        event: 'ui_interaction',
        location: 'pdp_gallery',
        detail: 'Select Image: ',
        type: 'thumbnail'
      }
    },
    pdpBuyBox: {
      addToCart: {
        event: 'add_to_cart_click',
        location: 'pdp_buy_box',
        detail: 'Add: ',
        type: 'button'
      },
      variantDropdown: {
        event: 'ui_interaction',
        location: 'pdp_buy_box',
        detail: '',
        type: 'tab'
      },
      buyItNow: {
        event: 'ui_interaction',
        location: 'pdp_buy_box',
        detail: 'Express Checkout',
        type: 'button'
      },
      shareOpen: {
        event: 'ui_interaction',
        location: 'pdp_buy_box',
        detail: 'Open Share Menu',
        type: 'icon'
      }
    },
    pdpInfo: {
      tabView: {
        event: 'ui_interaction',
        location: 'pdp_info',
        detail: 'View Tab: ',
        type: 'tab'
      }
    },
    homepageCategories: {
      categoryTile: {
        event: 'promotion_click',
        location: 'homepage_categories',
        detail: 'Visual: ',
        type: 'promo_card'
      }
    },
    homepageContent: {
      articleCard: {
        event: 'promotion_click',
        location: 'homepage_content',
        detail: 'Article: ',
        type: 'article_card'
      },
      videoPlay: {
        event: 'ui_interaction',
        location: 'homepage_content',
        detail: 'Play Video: ',
        type: 'video'
      },
      productClickCard: {
        event: 'product_click',
        location: 'homepage_content',
        detail: 'Featured: ',
        type: 'product_card'
      },
      productClickView: {
        event: 'product_click',
        location: 'homepage_content',
        detail: 'View Featured: ',
        type: 'button'
      },
      addToCart: {
        event: 'add_to_cart_click',
        location: 'homepage_content',
        detail: 'Add Featured: ',
        type: 'button'
      }
    },
    homepagePromos: {
      banner: {
        event: 'promotion_click',
        location: 'homepage_promos',
        detail: 'Promo: ',
        type: 'promo_banner'
      },
      ctaButton: {
        event: 'promotion_click',
        location: 'homepage_promos',
        detail: 'Promo: ',
        type: 'promo_button'
      }
    },
    homepageProducts: {
      productClickCard: {
        event: 'product_click',
        location: 'homepage_products',
        detail: '',
        type: 'product_card'
      },
      productClickView: {
        event: 'product_click',
        location: 'homepage_products',
        detail: 'View: ',
        type: 'button'
      },
      addToCart: {
        event: 'add_to_cart_click',
        location: 'homepage_products',
        detail: 'Add: ',
        type: 'button'
      }
    },
    productGrid: {
      productClickCard: {
        event: 'product_click',
        location: 'product_grid',
        detail: '',
        type: 'product_card'
      },
      productClickView: {
        event: 'product_click',
        location: 'product_grid',
        detail: 'View: ',
        type: 'button'
      },
      addToCart: {
        event: 'add_to_cart_click',
        location: 'product_grid',
        detail: 'Add: ',
        type: 'button'
      }
    },
    recommendationGrid: {
      productClickCard: {
        event: 'product_click',
        location: 'recommendation_grid',
        detail: 'Recs: ',
        type: 'product_card'
      },
      productClickView: {
        event: 'product_click',
        location: 'recommendation_grid',
        detail: 'Recs: View ',
        type: 'button'
      },
      addToCart: {
        event: 'add_to_cart_click',
        location: 'recommendation_grid',
        detail: 'Recs: Quick Add ',
        type: 'button'
      }
    },
    padFinder: {
      close: {
        event: 'ui_interaction',
        location: 'pad_finder_modal',
        detail: 'Close Pad Finder',
        type: 'icon'
      },
      brandSelect: {
        event: 'ui_interaction',
        location: 'pad_finder_modal',
        detail: 'Select Brand: ',
        type: 'dropdown'
      },
      modelSelect: {
        event: 'ui_interaction',
        location: 'pad_finder_modal',
        detail: 'Select Model: ',
        type: 'dropdown'
      },
      showProducts: {
        event: 'ui_interaction',
        location: 'pad_finder_modal',
        detail: 'Submit Pad Search',
        type: 'button'
      },
      resetSearch: {
        event: 'ui_interaction',
        location: 'pad_finder_modal',
        detail: 'Reset Search',
        type: 'button'
      },
      addYourModel: {
        event: 'ui_interaction',
        location: 'pad_finder_modal',
        detail: 'Open Add Model Request',
        type: 'button'
      }
    },
    addModelModal: {
      close: {
        event: 'ui_interaction',
        location: 'add_model_modal',
        detail: 'Close Add Model Modal',
        type: 'icon'
      },
      brandInput: {
        event: 'form_interaction',
        location: 'add_model_modal',
        detail: 'Input: Brand Name',
        type: 'text_input'
      },
      modelInput: {
        event: 'form_interaction',
        location: 'add_model_modal',
        detail: 'Input: Headphone Model',
        type: 'text_input'
      },
      emailInput: {
        event: 'form_interaction',
        location: 'add_model_modal',
        detail: 'Input: Email Address',
        type: 'text_input'
      },
      notifySubmit: {
        event: 'form_interaction',
        location: 'add_model_modal',
        detail: 'Submit Notification Request',
        type: 'button'
      }
    },
    footer: {
      newsletterInput: {
        event: 'form_interaction',
        location: 'footer_nav',
        detail: 'Input: Newsletter Email',
        type: 'text_input'
      },
      newsletterSubmit: {
        event: 'form_interaction',
        location: 'footer_nav',
        detail: 'Submit Newsletter',
        type: 'button'
      }
    },
    bundleStep1: {
      productClick: {
        event: 'product_click',
        location: 'bundle_step_1_grid',
        detail: 'View: ',
        type: 'product_card'
      },
      variantSelect: {
        event: 'ui_interaction',
        location: 'bundle_step_1_grid',
        detail: 'Select Length: ',
        type: 'button'
      },
      addToBundle: {
        event: 'ui_interaction',
        location: 'bundle_step_1_grid',
        detail: 'Add to Bundle: ',
        type: 'button'
      }
    },
    bundleStep2: {
      productClick: {
        event: 'product_click',
        location: 'bundle_step_2_grid',
        detail: 'View: ',
        type: 'product_card'
      },
      addToBundle: {
        event: 'ui_interaction',
        location: 'bundle_step_2_grid',
        detail: 'Add to Bundle: ',
        type: 'button'
      }
    },
    bundleSidebar: {
      addToCart: {
        event: 'add_to_cart_click',
        location: 'bundle_summary_sidebar',
        detail: 'Add Complete Bundle: ',
        type: 'button'
      }
    },
    cartDrawer: {
      close: {
        event: 'ui_interaction',
        location: 'cart_drawer',
        detail: 'Close Cart',
        type: 'icon'
      },
      productClick: {
        event: 'product_click',
        location: 'cart_drawer',
        detail: '',
        type: 'icon'
      },
      recentProductClick: {
        event: 'product_click',
        location: 'cart_drawer',
        detail: 'Recent: ',
        type: 'product_card'
      },
      changeQty: {
        event: 'ui_interaction',
        location: 'cart_drawer',
        detail: 'Change Qty: ',
        type: 'button'
      },
      remove: {
        event: 'remove_from_cart',
        location: 'cart_drawer',
        detail: 'Remove: ',
        type: 'link'
      },
      orderNote: {
        event: 'ui_interaction',
        location: 'cart_drawer',
        detail: 'Open Order Note',
        type: 'tab'
      },
      discount: {
        event: 'ui_interaction',
        location: 'cart_drawer',
        detail: 'Open Discount Input',
        type: 'tab'
      },
      checkout: {
        event: 'ui_interaction',
        location: 'cart_drawer',
        detail: 'Click Checkout',
        type: 'button'
      }
    },
    bottomSticky: {
      promoOpen: {
        event: 'ui_interaction',
        location: 'bottom_sticky',
        detail: 'Open Promo: 15% Off',
        type: 'button'
      },
      promoClose: {
        event: 'ui_interaction',
        location: 'bottom_sticky',
        detail: 'Close Promo: 15% Off',
        type: 'icon'
      }
    }
  };

  window.pushGA4 = pushGA4;
  window.buildGA4Payload = buildPayload;
  window.GA4_EVENTS = GA4_EVENTS;
  window.pushGA4Deferred = pushGA4Deferred;
  window.flushDeferredGA4 = flushDeferredGA4;
  window.isRedirectingLink = isRedirectingLink;
  window.markRedirectingLinks = markRedirectingLinks;

  // GA4 ecommerce: add_to_cart
  // Fires from all add-to-cart sources (PDP, quick-add, collection, bundle)
  // via the 'ajaxProduct:added' custom event dispatched by theme.js after every successful cart/add fetch.
  if (typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('ajaxProduct:added', function(e) {
      var data = e.detail && e.detail.product;
      if (!data) return;

      // Shopify returns a single line item for standard forms, or { items: [...] } for bundle adds
      var lineItems = data.items ? data.items : [data];
      var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      var totalValue = 0;

      var items = lineItems.map(function(product, idx) {
        var price = Math.round(product.price || 0) / 100;
        var compareAt = product.compare_at_price ? Math.round(product.compare_at_price) / 100 : 0;
        var discount = compareAt > price ? Math.round((compareAt - price) * 100) / 100 : 0;
        var quantity = product.quantity || 1;
        totalValue += price * quantity;

        var item = {
          item_id: product.sku || String(product.variant_id || product.id),
          item_name: product.title,
          index: idx,
          item_brand: product.vendor || '',
          item_category: product.product_type || '',
          price: price,
          quantity: quantity
        };
        if (discount > 0) item.discount = discount;
        if (product.variant_title && product.variant_title !== 'Default Title') {
          item.item_variant = product.variant_title;
        }
        return item;
      });

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          currency: currency,
          value: Math.round(totalValue * 100) / 100,
          items: items
        }
      });

      if (typeof console !== 'undefined' && console.log) {
        console.log('[GA4] add_to_cart', items);
      }
    });
  }

  // GA4 ecommerce: begin_checkout
  // Caches /cart.js on load and after every cart update, then pushes on checkout button click.
  // Covers both cart drawer (button[name="checkout"] inside #CartDrawer) and cart page.
  if (typeof document !== 'undefined' && document.addEventListener && typeof fetch !== 'undefined') {
    var _ga4CartCache = null;

    function _ga4FetchCart() {
      fetch('/cart.js')
        .then(function(r) { return r.json(); })
        .then(function(cart) { _ga4CartCache = cart; })
        .catch(function() {});
    }

    document.addEventListener('DOMContentLoaded', _ga4FetchCart);
    document.addEventListener('ajaxProduct:added', _ga4FetchCart);

    document.addEventListener('click', function(e) {
      if (!e.target || !e.target.closest) return;
      var btn = e.target.closest('button[name="checkout"]') || e.target.closest('.shopify-payment-button');
      if (!btn) return;
      if (!_ga4CartCache || !_ga4CartCache.items || !_ga4CartCache.items.length) return;

      var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || _ga4CartCache.currency || 'USD';

      var items = _ga4CartCache.items.map(function(item, idx) {
        var price = Math.round(item.price || 0) / 100;
        var originalPrice = item.original_price ? Math.round(item.original_price) / 100 : 0;
        var discount = originalPrice > price ? Math.round((originalPrice - price) * 100) / 100 : 0;

        var ga4Item = {
          item_id: item.sku || String(item.variant_id || item.product_id),
          item_name: item.product_title || item.title,
          index: idx,
          item_brand: item.vendor || '',
          item_category: item.product_type || '',
          price: price,
          quantity: item.quantity || 1
        };
        if (discount > 0) ga4Item.discount = discount;
        if (item.variant_title && item.variant_title !== 'Default Title') {
          ga4Item.item_variant = item.variant_title;
        }
        return ga4Item;
      });

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'begin_checkout',
        ecommerce: {
          currency: currency,
          value: Math.round(_ga4CartCache.total_price) / 100,
          items: items
        }
      });

      if (typeof console !== 'undefined' && console.log) {
        console.log('[GA4] begin_checkout', items);
      }
    });
  }

  // Klaviyo promo bubble: open (.needsclick click) and close (klaviyoForms close event)
  if (typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('click', function(e) {
      if (e.target && e.target.closest && e.target.closest('.needsclick')) {
        doPushGA4(buildPayload(GA4_EVENTS.bottomSticky.promoOpen));
      }
    });
  }
  if (typeof window !== 'undefined' && window.addEventListener) {
    window.addEventListener('klaviyoForms', function(e) {
      if (e.detail && e.detail.type === 'close') {
        doPushGA4(buildPayload(GA4_EVENTS.bottomSticky.promoClose));
      }
    });
  }
})();
